/**
 * AnalyticsGate — GDPR-compliant analytics consent bridge.
 *
 * Google Analytics 4 is now loaded via `index.html` with Google Consent
 * Mode v2 defaults (every signal set to `denied`). This component is only
 * responsible for flipping those flags to `granted`/`denied` based on the
 * user's cookie-banner choice.
 *
 * PostHog is still code-split and only pulled in after consent — it has no
 * consent-mode equivalent so the conservative approach is to not load it
 * at all until the user opts in.
 *
 * Why Consent Mode v2 for GA:
 *   - Google's tag-detection tools find the snippet on the first page fetch,
 *     so GA4 reports the property as "installed"
 *   - With `ad_storage` + `analytics_storage` denied, no cookies are set and
 *     no user-identifying events are recorded — GDPR Art. 7 happy
 *   - Once the user clicks Accept, a single `gtag('consent','update',…)` call
 *     backfills behaviour without reloading the page
 *   - Docs: https://developers.google.com/tag-platform/security/guides/consent
 */
import { useEffect, useRef } from 'react';
import { useConsent } from '@/contexts/ConsentContext';

const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';

function updateGoogleConsent(granted) {
  if (typeof window.gtag !== 'function') return;
  const state = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
}

export function AnalyticsGate() {
  const { hasConsent } = useConsent();
  const analyticsAllowed = hasConsent('analytics');
  const posthogInitialisedRef = useRef(false);
  const posthogRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // 1. Google Analytics — flip Consent Mode v2 flags. Idempotent: calling
    //    update() repeatedly with the same state is a no-op.
    updateGoogleConsent(analyticsAllowed);

    // 2. PostHog — code-split (~80KB), only pulled in after consent, torn
    //    down on withdrawal.
    //
    // ⚠ Session recording is INTENTIONALLY DISABLED.
    //
    // PostHog's `rrweb`-backed recorder serialises every DOM mutation on
    // the main thread. On real-time pages (live leaderboard, pool detail,
    // sweat page) the WebSocket pushes a fresh entries-array every minute
    // which triggers dozens of mutations per tick — plus framer-motion
    // `layout` animations on every row. On mobile Android Chrome this
    // accumulates encoder backlog + heap pressure until the GC freezes
    // the tab for 5-15 seconds at random.
    //
    // Confirmed via Feb 2026 user-reported production freezes on the
    // pool + leaderboard pages. Disabling session_recording resolved it
    // immediately. Event capture (clicks, page views, identify(), etc.)
    // is unaffected by this flag.
    //
    // If we ever re-enable: gate to desktop only (`isMobile && false`),
    // sample at <=10%, and mask the live leaderboard with a
    // `ph-no-capture` class so the recorder skips it.
    if (analyticsAllowed && !posthogInitialisedRef.current) {
      import('posthog-js').then(({ default: posthog }) => {
        if (cancelled) return;
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          person_profiles: 'identified_only',
          disable_session_recording: true,
          capture_pageview: true,
          autocapture: true,
        });
        posthogRef.current = posthog;
        posthogInitialisedRef.current = true;
      });
    } else if (!analyticsAllowed && posthogRef.current) {
      try {
        posthogRef.current.opt_out_capturing?.();
        posthogRef.current.reset?.();
      } catch {
        /* PostHog may not be fully loaded yet */
      }
    }

    return () => { cancelled = true; };
  }, [analyticsAllowed]);

  return null;
}
